import {getTestProps} from '@benchling/js-test-utils';
import {asyncNoop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';
import {ReactFlowProvider} from 'reactflow';

import * as analytics from 'analytics/Analytics';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessSingletonTaskSchemaName} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import notifications from 'notifications';
import AutosaveManager from 'procedures/redux/autosaveManager';
import * as ProcessFlowchartConfigDAO from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdditionalPersistedData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {
  ProcessFlowchartErrorType,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import useOnMount from 'ui-library/hooks/useOnMount';
import useWarnOnNavigate from 'ui-library/hooks/useWarnOnNavigate';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartSidepanelManager from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelManager';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';
import {ProcessFlowchartConfigVersionForEditorGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigVersionForEditorGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {doSchemaMapperConfigsUseConditionalMapping} from '../conditionalMappingUtils';
import buildNetworkFlowchartFromStaticData from './buildNetworkFlowchartConfigFromStaticData';
import buildStaticDataFromFlowchartConfigGraphQL from './buildStaticDataFromFlowchartConfigGraphQL';
import {
  ProcessFlowchartConfigWithValidSchemaGQL,
  StaticTemplateFlowchart,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorContent from './ProcessFlowchartConfigEditorContent';
import ProcessFlowchartConfigEditorHeader from './ProcessFlowchartConfigEditorHeader';
import {HeaderConfigAdditionalProps} from './ProcessFlowchartConfigEditorHeaderTypes';
import {FlowchartNode} from './processFlowchartConfigEditorNodeTypes';
import {getFlowchartConfigSafely} from './processFlowchartConfigEditorUtils';
import {getLockedStateAndErrorMessage} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {saveHandlerFactory} from './redux/flowchartConfigsSaveHandler';
import {
  FlowchartConfigStateManager,
  FlowchartConfigStateManagerChildrenProps,
  FlowchartConfigWithPartialMetadata,
  calculateSaveState,
} from './redux/FlowchartConfigStateManager';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext} from './SingletonTaskSchemasContext';
import {ConfigurableTaskDefinitionGQL, useAllSchemasByIdWithSingletonSchemas} from './useAllSchemasById';
import {
  useFlowchartValidationAndAutosaverControl,
  ValidationModule,
} from './useFlowchartValidationAndAutosaverControl';

export enum FlowchartConfigTab {
  SCHEMA_MAPPER_CONFIGS = 'SCHEMA_MAPPER_CONFIGS',
  TEMPLATE_FLOWCHART = 'TEMPLATE_FLOWCHART',
}
export interface ProcessFlowchartConfigEditorControllerProps {
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  flowchartConfig: ProcessFlowchartConfigWithValidSchemaGQL;
  flowchartConfigVersion: ProcessFlowchartConfigVersionForEditorGraphQL | null;
  locked: boolean;
  lockErrorMessage?: string;
  refetchFlowchartConfig?: () => void;
  ownerToRegistryMap?: Map<string, string>;
  headerConfigAdditionalProps?: HeaderConfigAdditionalProps;
}

/**
 * Process FlowchartConfig editor that displays the specified flowchart config version.
 *
 * Ensures queries have completed before rendering the editor.
 */
export default function ProcessFlowchartConfigEditorController(
  props: ProcessFlowchartConfigEditorControllerProps
): JSX.Element {
  const rootTaskSchema = useMemo(() => props.flowchartConfig.schema, [props.flowchartConfig.schema]);

  const {
    schemasById: allSchemasById,
    processTaskDefinitions,
    singletonTaskSchemasByName: _singletonTaskSchemasByName,
  } = useAllSchemasByIdWithSingletonSchemas({
    flowchartEditorConfigurator: props.flowchartEditorConfigurator,
    rootTaskSchema,
  });

  const singletonTaskSchemasByName = useMemo(
    () =>
      _singletonTaskSchemasByName ??
      ({} as Record<ProcessSingletonTaskSchemaName, ProcessTaskSchemaWithoutFlowchartConfigGraphQL>),
    [_singletonTaskSchemasByName]
  );

  if (processTaskDefinitions == null || allSchemasById == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <div {...getTestProps({component: 'ProcessFlowchartConfigEditorController', element: 'root'})}>
      <SingletonTaskSchemasContext.Provider value={singletonTaskSchemasByName}>
        <ProcessFlowchartConfigDataTransformer
          {...props}
          rootTaskSchema={rootTaskSchema}
          processTaskDefinitions={processTaskDefinitions}
          singletonTaskSchemasByName={singletonTaskSchemasByName}
          allSchemasById={allSchemasById}
          refetchFlowchartConfig={props.refetchFlowchartConfig}
        />
      </SingletonTaskSchemasContext.Provider>
    </div>
  );
}

export interface ProcessFlowchartConfigDataTransformerProps extends ProcessFlowchartConfigEditorControllerProps {
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  processTaskDefinitions: Array<ConfigurableTaskDefinitionGQL>;
  singletonTaskSchemasByName: Record<
    ProcessSingletonTaskSchemaName,
    ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  >;
  allSchemasById: SchemaById;
}

/**
 * Converts GraphQL flowchart shape to a static representation of nodes and edges to pass to the flowchart editor.
 */
function ProcessFlowchartConfigDataTransformer(
  props: ProcessFlowchartConfigDataTransformerProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<FlowchartConfigTab>(FlowchartConfigTab.TEMPLATE_FLOWCHART);

  const {
    nodesStaticData,
    edgesStaticData,
    schemaMapperConfigs: initialSchemaMapperConfigs,
    customMappingDataByEdgeId: initialCustomMappingDataByEdgeId,
    transitionSourceByNodeIdPair: initialTransitionSourceByNodeIdPair,
    availableTaskSchemas,
    hasChainedRouters: initialFlowchartHasChainedRouters,
    laBlockingReason,
    cidToIdMap: initialCidToIdMap,
  } = useMemo(
    () =>
      buildStaticDataFromFlowchartConfigGraphQL({
        flowchartConfigVersion: props.flowchartConfigVersion,
        rootTaskSchema: props.rootTaskSchema,
        rootTaskDefinition: props.flowchartConfig.taskDefinition,
        allTaskSchemas: [
          props.rootTaskSchema,
          ...props.processTaskDefinitions.map((definition) => definition.latestVersion.processTaskSchema),
          ...Object.values(props.singletonTaskSchemasByName),
        ],
        flowchartEditorConfigurator: props.flowchartEditorConfigurator,
      }),
    [
      props.flowchartConfigVersion,
      props.processTaskDefinitions,
      props.rootTaskSchema,
      props.flowchartConfig.taskDefinition,
      props.singletonTaskSchemasByName,
      props.flowchartEditorConfigurator,
    ]
  );

  const [didInitialConfigHaveConditionalMapping, _setDidInitialConfigHaveConditionalMapping] = useState(
    doSchemaMapperConfigsUseConditionalMapping(initialSchemaMapperConfigs ?? [])
  );
  const [didInitialFlowchartHaveChainedRouters, _setDidInitialFlowchartHaveChainedRouters] = useState(
    initialFlowchartHasChainedRouters
  );

  const taskDefinitionsForSidepanel = useMemo(() => {
    const nodeExecutionTypes = props.flowchartEditorConfigurator.allowedNodeExecutionTypes;
    const taskDefinitionsForSidepanel = props.processTaskDefinitions.filter((definition) =>
      nodeExecutionTypes.includes(definition.latestVersion.processTaskSchema.executionType)
    );
    return taskDefinitionsForSidepanel;
  }, [props.flowchartEditorConfigurator, props.processTaskDefinitions]);

  return (
    <div {...getTestProps({component: 'ProcessFlowchartConfigDataTransformer', element: 'root'})}>
      <FlowchartConfigStateManager
        flowchartConfigId={props.flowchartConfig.id}
        persistedFlowchartConfig={{
          nodesStaticData,
          edgesStaticData,
        }}
        flowchartConfigMetadata={{
          transitions: {},
          schemaMapperConfigs: initialSchemaMapperConfigs,
          rootTaskSchema: props.rootTaskSchema,
          customMappingDataByEdgeId: initialCustomMappingDataByEdgeId,
          transitionSourceByNodeIdPair: initialTransitionSourceByNodeIdPair,
          interactionState: {
            selectedNodeIds: new Set([]),
            selectedRowIds: new Set([]),
            selectedEdgeIds: new Set([]),
            edgeLabelsByEdgeId: {},
          },
          currentVersionId: props.flowchartConfig.flowchartConfigVersion?.id ?? null,
          didInitialFlowchartHaveChainedRouters: initialFlowchartHasChainedRouters,
          flowchartTypeName: props.flowchartEditorConfigurator.flowchartTypeName,
          options: {
            isFlowchartModifiableOnCreation:
              props.flowchartConfigVersion?.isFlowchartModifiableOnCreation ?? true,
            isFlowchartModifiableAfterCreation:
              props.flowchartConfigVersion?.isFlowchartModifiableAfterCreation ?? false,
          },
        }}
      >
        {(flowchartConfigReduxData) => (
          <PresentationalProcessFlowchartConfigEditor
            {...props}
            taskDefinitionsForSidepanel={taskDefinitionsForSidepanel}
            didInitialConfigHaveConditionalMapping={didInitialConfigHaveConditionalMapping}
            didInitialFlowchartHaveChainedRouters={didInitialFlowchartHaveChainedRouters}
            laBlockingReason={laBlockingReason}
            availableTaskSchemas={availableTaskSchemas}
            activeTab={activeTab}
            locked={props.locked}
            setActiveTab={setActiveTab}
            cidToIdMap={initialCidToIdMap}
            {...flowchartConfigReduxData}
          />
        )}
      </FlowchartConfigStateManager>
    </div>
  );
}

function buildFlowchartConfigSpec(
  staticFlowchart: StaticTemplateFlowchart,
  metadata: FlowchartConfigMetadata,
  flowchartConfigId: string
): ProcessFlowchartConfigDAO.ProcessFlowchartConfigSpec {
  return buildNetworkFlowchartFromStaticData({
    staticFlowchart,
    options: {
      transitions: metadata.transitions,
      schemaMapperConfigs: metadata.schemaMapperConfigs,
      rootTaskSchema: metadata.rootTaskSchema,
      customMappingDataByEdgeId: metadata.customMappingDataByEdgeId,
      transitionSourceByNodeIdPair: metadata.transitionSourceByNodeIdPair,
      flowchartConfigId,
      options: metadata.options,
    },
  });
}

/**
 * When we unmount, we need to create a clean save handler that prevents multiple saves
 * and doesn't perform internal bookkeeping
 */
function saveFlowchartConfigWhenUnmountedFactory(
  flowchartConfigId: string,
  lastPersistedSpecRef: React.MutableRefObject<ProcessFlowchartConfigDAO.ProcessFlowchartConfigSpec | null>
): (flowchartConfig: FlowchartConfigWithMetadata) => Promise<{
  flowchartConfigGQL: null;
  snapshotFlowchartConfigWithMetadata: FlowchartConfigWithMetadataAndRootTaskSchema;
  additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
}> {
  let saved = false;

  return async (snapshotFlowchartConfigWithMetadata: FlowchartConfigWithMetadataAndRootTaskSchema) => {
    // Return value is meaningless since we are unmounted
    const defaultReturn = {
      flowchartConfigGQL: null,
      snapshotFlowchartConfigWithMetadata,
      additionalPersistedData: {
        cidToIdMap: {},
        // We don't care about updating this since we are unmounted, so return the current version id
        currentVersionId: snapshotFlowchartConfigWithMetadata.metadata.currentVersionId ?? null,
      },
    };

    if (saved) {
      // Ensure that we don't save the flowchart config multiple times after it's been unmounted
      return defaultReturn;
    }

    const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
      snapshotFlowchartConfigWithMetadata.metadata.flowchartTypeName
    );

    const validationErrors = ValidationModule.validateFlowchartData(
      snapshotFlowchartConfigWithMetadata.staticFlowchart,
      flowchartConfigId,
      snapshotFlowchartConfigWithMetadata.metadata,
      snapshotFlowchartConfigWithMetadata.metadata.rootTaskSchema,
      snapshotFlowchartConfigWithMetadata.metadata.didInitialFlowchartHaveChainedRouters,
      plugin
    );
    const hasBlockingValidationErrors = validationErrors.some(
      (error: ProcessFlowchartValidationError) => error.blocksPersistence
    );
    if (hasBlockingValidationErrors) {
      return defaultReturn;
    }

    const flowchartConfigSpec = buildFlowchartConfigSpec(
      snapshotFlowchartConfigWithMetadata.staticFlowchart,
      snapshotFlowchartConfigWithMetadata.metadata,
      flowchartConfigId
    );

    if (lastPersistedSpecRef.current != null && isEqual(flowchartConfigSpec, lastPersistedSpecRef.current)) {
      saved = true;
      return defaultReturn;
    }

    await ProcessFlowchartConfigDAO.updateProcessFlowchartConfig({
      flowchartConfigId,
      flowchartConfigSpec,
      currentVersionId: snapshotFlowchartConfigWithMetadata.metadata.currentVersionId,
    });
    lastPersistedSpecRef.current = flowchartConfigSpec;
    saved = true;

    return defaultReturn;
  };
}

export type PresentationalProcessFlowchartConfigEditorProps = ProcessFlowchartConfigDataTransformerProps &
  FlowchartConfigStateManagerChildrenProps & {
    taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
    didInitialConfigHaveConditionalMapping: boolean;
    didInitialFlowchartHaveChainedRouters: boolean;
    laBlockingReason: string | null;
    availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
    cidToIdMap: CidIdMap;
    activeTab: FlowchartConfigTab;
    setActiveTab: (tab: FlowchartConfigTab) => void;
  };

/**
 * Presentational component that renders the flowchart editor page with tabs and handles save logic.
 * Contains the UI layout and event handlers but delegates state management to redux props.
 */
function PresentationalProcessFlowchartConfigEditor(
  props: PresentationalProcessFlowchartConfigEditorProps
): JSX.Element {
  const {
    draftFlowchartConfigAndMetadata,
    draftFlowchartConfigAndMetadataRef,
    setFlowchartConfigAndMetadata,
    liveStateChanges,
    lastSaveError,
  } = props;

  const {computedLocked, computedLockErrorMessage} = getLockedStateAndErrorMessage(
    props.locked,
    props.laBlockingReason,
    props.lockErrorMessage,
    props.liveStateChanges
  );

  const getNodeByIdRef = useRef<(nodeId: string) => FlowchartNode | null>(() => null);
  const getNodeNameById = useCallback(
    (nodeId: string): string | null => getNodeByIdRef.current(nodeId)?.data.nodeName ?? null,
    []
  );

  const [isFlowchartConfigDirty, _setIsFlowchartConfigDirty] = liveStateChanges.dirtyState;
  const [isFlowchartConfigSaveInFlight, _setIsFlowchartConfigSaveInFlight] =
    liveStateChanges.isSaveInFlightState;
  const [isFlowchartConfigAutosaverPaused, setIsFlowchartConfigAutosaverPaused] =
    liveStateChanges.isAutosaverPausedState;

  const lastPersistedSpecRef = useRef<ProcessFlowchartConfigDAO.ProcessFlowchartConfigSpec | null>(null);

  const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
    props.flowchartEditorConfigurator.flowchartTypeName
  );

  const {
    validationErrors: baseValidationErrors,
    validationErrorsRef: baseValidationErrorsRef,
    validateFlowchart,
  } = useFlowchartValidationAndAutosaverControl({
    draftFlowchartConfig: draftFlowchartConfigAndMetadata.staticFlowchart,
    flowchartConfigId: props.flowchartConfig.id,
    flowchartConfigMetadata: draftFlowchartConfigAndMetadata.metadata,
    rootTaskSchema: props.rootTaskSchema,
    taskDefinitions: props.taskDefinitionsForSidepanel,
    setIsFlowchartConfigAutosaverPaused,
    didInitialFlowchartHaveChainedRouters: props.didInitialFlowchartHaveChainedRouters,
    plugin,
  });

  useOnMount(() => {
    if (lastPersistedSpecRef.current == null) {
      lastPersistedSpecRef.current = buildFlowchartConfigSpec(
        props.persistedFlowchartConfig,
        props.flowchartConfigMetadata,
        props.flowchartConfig.id
      );
    }
  });

  // We don't need to block if the flowchart is just dirty, since we can save on unmount
  // We do need to block if there are paused conditions or validation errors that would prevent a save
  useWarnOnNavigate(
    isFlowchartConfigAutosaverPaused ||
      baseValidationErrorsRef.current.some((error) => error.blocksPersistence),
    'You have unsaved changes to this flowchart. Are you sure you want to leave?'
  );

  const handleSave = useCallback(
    async (
      flowchartConfigWithMetadata: FlowchartConfigWithMetadata
    ): Promise<null | {
      flowchartConfigGQL: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL | null;
      snapshotFlowchartConfigWithMetadata: FlowchartConfigWithMetadataAndRootTaskSchema;
      additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
    }> => {
      const anyBlockingValidationErrors = baseValidationErrorsRef.current.some(
        (error) => error.blocksPersistence
      );
      if (anyBlockingValidationErrors) {
        // We can't save the flowchart config if there are blocking validation errors
        // So we return the current flowchart config with the validation errors
        // This is primarily for the saveImmediately flow, which is outside of the autosaver blocked/paused logic
        // Ensure the flowchart can still be updated so that validation errors can be resolved
        notifications.error('Cannot save with validation errors. Resolve errors to save changes.');
        return null;
      }
      const flowchartConfigSpec = buildFlowchartConfigSpec(
        flowchartConfigWithMetadata.staticFlowchart,
        flowchartConfigWithMetadata.metadata,
        props.flowchartConfig.id
      );

      if (
        lastPersistedSpecRef.current != null &&
        isEqual(flowchartConfigSpec, lastPersistedSpecRef.current)
      ) {
        return null;
      }
      const {cidToIdMap: newCidToIdMap, flowchartConfig: flowchartConfigGQL} =
        await ProcessFlowchartConfigDAO.updateProcessFlowchartConfig({
          flowchartConfigId: props.flowchartConfig.id,
          flowchartConfigSpec,
          currentVersionId: flowchartConfigWithMetadata.metadata.currentVersionId,
        });
      lastPersistedSpecRef.current = flowchartConfigSpec;

      return {
        flowchartConfigGQL,
        snapshotFlowchartConfigWithMetadata: getFlowchartConfigSafely(flowchartConfigWithMetadata),
        additionalPersistedData: {
          cidToIdMap: newCidToIdMap,
          currentVersionId: flowchartConfigGQL.flowchartConfigVersion?.id ?? null,
        },
      };
    },
    [props.flowchartConfig.id, baseValidationErrorsRef, lastPersistedSpecRef]
  );

  // After a save completes, set the draft flowchart config in the redux store
  // The updater function is responsible for combining the client side flowchart config with the server side information
  const setPendingDraftFlowchartUpdate = useCallback(
    async (
      update: null | {
        updater: (
          latestDraftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata
        ) => Promise<FlowchartConfigWithMetadata>;
      }
    ): Promise<void> => {
      if (update != null) {
        const updatedFlowchartConfigAndMetadata = await update.updater(
          draftFlowchartConfigAndMetadataRef.current
        );
        setFlowchartConfigAndMetadata({
          flowchartConfigWithMetadata: {
            staticFlowchart: updatedFlowchartConfigAndMetadata.staticFlowchart,
            flowchartConfigMetadata: updatedFlowchartConfigAndMetadata.metadata,
          },
          skipSettingDirtyState: true,
        });
      }
    },
    [setFlowchartConfigAndMetadata, draftFlowchartConfigAndMetadataRef]
  );

  useOnMount(() => {
    AutosaveManager.updateSaveHandler(
      props.flowchartConfig.id,
      saveHandlerFactory(handleSave, setPendingDraftFlowchartUpdate, props.flowchartConfig.id)
    );

    // On unmount, we immediately swap out to a "clean" save handler, which performs no internal bookkeeping
    return () => {
      AutosaveManager.updateSaveHandler(
        props.flowchartConfig.id,
        saveHandlerFactory(
          saveFlowchartConfigWhenUnmountedFactory(props.flowchartConfig.id, lastPersistedSpecRef),
          asyncNoop,
          props.flowchartConfig.id
        )
      );
    };
  });

  // Synchronize the computed locked state (including laBlockingReason and autosaver blocked state)
  // with the Redux isReadOnly state. When locked, the autosave system will skip saves.
  const [_isReadOnly, setIsReadOnly] = liveStateChanges.readonlyState;
  useEffect(() => {
    setIsReadOnly(computedLocked);
  }, [computedLocked, setIsReadOnly]);

  /**
   * Sets flowchart and metadata state in the redux store. Can handle both autosave and save immediately.
   * @param getNewFlowchartConfigAndMetadata - A function that returns a new flowchart config and metadata.
   * @param saveImmediately - Whether to save the flowchart config immediately.
   */
  const setDraftFlowchartConfigAndMetadata = useCallback(
    (
      getNewFlowchartConfigAndMetadata: (
        currentFlowchartConfigAndMetadata: FlowchartConfigWithMetadata
      ) => FlowchartConfigWithPartialMetadata,
      saveImmediately: boolean = false,
      skipSettingDirtyState: boolean = false
    ): Promise<FlowchartConfigMetadata | null> => {
      const executeChanges = (): void => {
        const currentFlowchartConfigAndMetadata = draftFlowchartConfigAndMetadataRef.current;
        const partialUpdate = getNewFlowchartConfigAndMetadata(currentFlowchartConfigAndMetadata);
        setFlowchartConfigAndMetadata({
          flowchartConfigWithMetadata: partialUpdate,
          skipSettingDirtyState,
        });
        validateFlowchart(draftFlowchartConfigAndMetadataRef.current);
      };

      if (saveImmediately) {
        return AutosaveManager.saveImmediately(props.flowchartConfig.id, executeChanges, 'Saving...');
      } else {
        executeChanges();
        return Promise.resolve(null);
      }
    },
    [
      setFlowchartConfigAndMetadata,
      draftFlowchartConfigAndMetadataRef,
      props.flowchartConfig.id,
      validateFlowchart,
    ]
  );

  const archivedSchemas =
    props.flowchartConfigVersion?.availableSchemas.filter(
      (schema) => !isInaccessibleInfo(schema) && schema.archiveRecord != null
    ) ?? [];

  const archivedSchemaNames = archivedSchemas.map((s) => (isInaccessibleInfo(s) ? '(Inaccessible)' : s.name));

  const validationErrors = useMemo(() => {
    if (archivedSchemas.length > 0) {
      const archivedSchemaError: ProcessFlowchartValidationError = {
        object: {
          id: props.flowchartConfig.id,
          type: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
        },
        errorMessage: `This flowchart uses one or more archived task schemas. Users can't create this flowchart until you replace or unarchive the following task schema(s): ${archivedSchemaNames.join(', ')}`,
        errorType: ProcessFlowchartErrorType.UNKNOWN,
        blocksPersistence: false,
      };
      return [...baseValidationErrors, archivedSchemaError];
    }
    // If we start to add multiple "base validation errors" here we should investigate
    // just moving these validations into our core validation logic
    return baseValidationErrors;
  }, [baseValidationErrors, archivedSchemas.length, archivedSchemaNames, props.flowchartConfig.id]);

  const [isInitialMount, setIsInitialMount] = useState(true);

  useOnMount(() => {
    setIsInitialMount(false);
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPENED_HORIZONTAL_FLOWCHART_EDITOR, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      flowchartType: props.flowchartEditorConfigurator.flowchartTypeName,
      flowchartConfigId: props.flowchartConfig.id,
    });
  });

  const {saveState, customSaveStatusMessage} = useMemo(
    () =>
      calculateSaveState({
        isSaveInFlight: isFlowchartConfigSaveInFlight,
        isDirty: isFlowchartConfigDirty,
        validationErrors,
        lastSaveError,
        isInitialMount,
      }),
    [isFlowchartConfigSaveInFlight, isFlowchartConfigDirty, validationErrors, isInitialMount, lastSaveError]
  );

  const rootTaskSchema = useMemo(
    () =>
      // We can guarantee that the root task schema is present in the flowchart config metadata
      // at this point given the SaveManager's loading screen until this is present.
      // It has to be nullable in redux for the initialization state.
      getFlowchartConfigSafely(draftFlowchartConfigAndMetadata).metadata.rootTaskSchema,
    [draftFlowchartConfigAndMetadata]
  );

  const headerConfig = plugin?.headerConfig;

  return (
    <div
      className={css(styles.rootContainer)}
      {...getTestProps({component: 'PresentationalProcessFlowchartConfigEditor', element: 'root'})}
    >
      {headerConfig && (
        <ProcessFlowchartConfigEditorHeader
          headerConfig={headerConfig}
          flowchartConfig={props.flowchartConfig}
          rootTaskSchema={rootTaskSchema}
          saveState={saveState}
          hasUnsavedChanges={isFlowchartConfigDirty || isFlowchartConfigSaveInFlight}
          customSaveStatusMessage={customSaveStatusMessage}
          locked={computedLocked}
          headerConfigAdditionalProps={props.headerConfigAdditionalProps}
        />
      )}
      <div className={classNames('u-flex-grow', css(styles.tabContainer))}>
        <FlowchartSidepanelManager
          getNodeNameById={getNodeNameById}
          flowchartType={props.flowchartEditorConfigurator.flowchartTypeName}
        >
          <ReactFlowLockedContext.Provider value={computedLocked}>
            <ReactFlowProvider>
              <ProcessFlowchartConfigEditorContent
                getNodeByIdRef={getNodeByIdRef}
                flowchart={draftFlowchartConfigAndMetadata.staticFlowchart}
                flowchartEditorConfigurator={props.flowchartEditorConfigurator}
                allSchemasById={props.allSchemasById}
                rootTaskSchema={draftFlowchartConfigAndMetadata.metadata.rootTaskSchema!}
                flowchartTaskDefinitionId={props.flowchartConfig.taskDefinition.id}
                taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
                customMappingDataByEdgeId={draftFlowchartConfigAndMetadata.metadata.customMappingDataByEdgeId}
                transitionSourceByNodeIdPair={
                  draftFlowchartConfigAndMetadata.metadata.transitionSourceByNodeIdPair
                }
                didInitialConfigHaveConditionalMapping={props.didInitialConfigHaveConditionalMapping}
                didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
                refetchFlowchartConfig={props.refetchFlowchartConfig}
                liveStateChanges={props.liveStateChanges}
                locked={computedLocked}
                lockErrorMessage={computedLockErrorMessage}
                validationErrors={validationErrors}
                draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}
                setDraftFlowchartConfigAndMetadata={setDraftFlowchartConfigAndMetadata}
                ownerToRegistryMap={props.ownerToRegistryMap}
              />
            </ReactFlowProvider>
          </ReactFlowLockedContext.Provider>
        </FlowchartSidepanelManager>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  rootContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    minHeight: 0,
  },
  divider: {
    height: '1px',
    width: '100%',
    marginTop: '30px',
    left: '0px',
    background: `${Color.GRAY_200}`,
    position: 'absolute',
  },
  tabContainer: {
    padding: 0,
    flex: '1 1 auto',
    minHeight: 0,
  },
  selectItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
